import { useContext, useMemo, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, Title, Tooltip, Filler, Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useTrackedCases } from '../../../hooks/useTrackedCases';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';
import { maskName } from '../../../utils/privacy';
import MaskedName from '../../../components/MaskedName';
import { splitSpecies } from '../../../utils/species';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Filler, Legend);

interface Props { allData: any[] }

const STATUS_COLOR: Record<string, string> = {
  ongoing:   'text-violet-400',
  concluded: 'text-emerald-400',
  absconded: 'text-amber-400',
};

const MINI_CHART_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: {} } },
  scales: {
    x: { ticks: { color: '#6b7280', font: { size: 8 } }, grid: { color: 'rgba(255,255,255,0.03)' } },
    y: { ticks: { color: '#6b7280', font: { size: 8 }, maxTicksLimit: 4 }, grid: { color: 'rgba(255,255,255,0.05)' }, beginAtZero: true },
  },
};

export default function SpeciesWatchView({ allData }: Props) {
  const { state } = useContext(AuthContext);
  const role = state.userData?.role?.toLowerCase() ?? 'guest';
  const [selected, setSelected] = useState<string | null>(null);
  const { trackedIds, toggle, isLoggedIn } = useTrackedCases();

  /* ── Build species map ────────────────────────────────────────────── */
  const speciesMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    allData.forEach((d: any) => {
      splitSpecies(d.species).forEach((s) => {
        const key = s.toLowerCase();
        if (!map[key]) map[key] = [];
        map[key].push(d);
      });
    });
    return map;
  }, [allData]);

  const speciesList = useMemo(() =>
    Object.entries(speciesMap)
      .map(([name, cases]) => ({ name, cases }))
      .sort((a, b) => b.cases.length - a.cases.length),
    [speciesMap]
  );

  const detail = selected ? speciesMap[selected] : null;

  function statusCount(cases: any[], s: string) {
    return cases.filter((c: any) => c.caseStatus?.toLowerCase() === s).length;
  }

  /* ── Year-over-year trend for selected species ────────────────────── */
  const trendData = useMemo(() => {
    if (!detail) return null;
    const yrs: Record<string, number> = {};
    detail.forEach((c: any) => {
      if (c.dateOfSeizure) {
        const y = new Date(c.dateOfSeizure).getFullYear();
        if (y > 2000) yrs[String(y)] = (yrs[String(y)] || 0) + 1;
      }
    });
    const labels = Object.keys(yrs).sort();
    return {
      labels,
      datasets: [{
        label: 'Incidents',
        data: labels.map((y) => yrs[y]),
        borderColor: 'rgba(74,222,128,0.9)',
        backgroundColor: 'rgba(74,222,128,0.12)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(74,222,128,1)',
      }],
    };
  }, [detail]);

  /* ── Outcome breakdown for selected species ───────────────────────── */
  const outcomeData = useMemo(() => {
    if (!detail) return null;
    const ongoing   = statusCount(detail, 'ongoing');
    const concluded = statusCount(detail, 'concluded');
    return {
      labels: ['Ongoing', 'Concluded'],
      datasets: [{
        label: 'Cases',
        data: [ongoing, concluded],
        backgroundColor: ['rgba(167,139,250,0.75)', 'rgba(52,211,153,0.75)'],
        borderColor:     ['rgba(167,139,250,1)',    'rgba(52,211,153,1)'],
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  }, [detail]);

  /* ── Conviction rate ──────────────────────────────────────────────── */
  const convictionRate = useMemo(() => {
    if (!detail || detail.length === 0) return null;
    const concluded = statusCount(detail, 'concluded');
    return Math.round((concluded / detail.length) * 100);
  }, [detail]);

  /* ── Top locations for selected species ──────────────────────────── */
  const topLocations = useMemo(() => {
    if (!detail) return [];
    const counts: Record<string, number> = {};
    detail.forEach((c: any) => {
      const l = c.location?.trim();
      if (l) counts[l] = (counts[l] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [detail]);

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Species Watch</h2>
        <p className="text-white/30 text-xs mt-0.5">Per-species incident tracking, trends and threat analysis</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Species list ──────────────────────────────────────────── */}
        <div className="rounded-xl border border-white/6 overflow-hidden flex flex-col" style={{ background: '#0a2e18' }}>
          <div className="px-4 py-3 border-b border-white/5 shrink-0">
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Tracked Species</p>
            <p className="text-white/25 text-[10px] mt-0.5">{speciesList.length} species &bull; {allData.length} total cases</p>
          </div>
          <div className="overflow-y-auto flex-1" style={{ maxHeight: 560, scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
            {speciesList.map(({ name, cases }) => {
              const isSelected = selected === name;
              const maxCount = speciesList[0]?.cases.length || 1;
              const rate = Math.round((statusCount(cases, 'concluded') / cases.length) * 100);
              return (
                <button
                  key={name}
                  onClick={() => setSelected(isSelected ? null : name)}
                  className={`w-full text-left px-4 py-3 border-b border-white/4 last:border-0 transition-all hover:bg-white/4 ${isSelected ? 'bg-green-500/8 border-l-2 border-l-green-500/50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-xs font-medium capitalize ${isSelected ? 'text-green-400' : 'text-white/70'}`}>{name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-white/25">{rate}% conv.</span>
                      <span className={`text-xs font-bold tabular-nums ${isSelected ? 'text-green-400' : 'text-white/50'}`}>{cases.length}</span>
                    </div>
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${isSelected ? 'bg-green-500' : 'bg-green-800'}`}
                      style={{ width: `${(cases.length / maxCount) * 100}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Detail panel ──────────────────────────────────────────── */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {!detail ? (
            <div className="h-full rounded-xl border border-white/6 flex flex-col items-center justify-center py-20 gap-3" style={{ background: '#0a2e18' }}>
              <div className="w-12 h-12 rounded-full border border-white/8 flex items-center justify-center">
                <span className="text-white/15 text-xl">&#9654;</span>
              </div>
              <p className="text-white/25 text-sm">Select a species to view intelligence</p>
              <p className="text-white/15 text-xs">Year-over-year trends, conviction rates, top locations</p>
            </div>
          ) : (
            <>
              {/* Species header + KPIs */}
              <div className="rounded-xl border border-white/6 px-5 py-4" style={{ background: '#0a2e18' }}>
                <p className="text-green-400 text-[10px] uppercase tracking-widest mb-1">Species Intelligence Profile</p>
                <h3 className="text-white/90 text-xl font-bold capitalize mb-3" style={{ fontFamily: 'var(--font-display)' }}>{selected}</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {[
                    { label: 'Total Incidents', value: detail.length,                       color: 'text-white/85' },
                    { label: 'Ongoing',          value: statusCount(detail, 'ongoing'),       color: 'text-violet-400' },
                    { label: 'Concluded',        value: statusCount(detail, 'concluded'),     color: 'text-emerald-400' },
                    { label: 'Conv. Rate',       value: `${convictionRate ?? 0}%`,            color: convictionRate! >= 50 ? 'text-emerald-400' : 'text-red-400' },
                  ].map((s) => (
                    <div key={s.label} className="flex flex-col items-center gap-0.5 px-4 py-2.5 rounded-lg shrink-0" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <span className={`text-xl font-bold tabular-nums ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</span>
                      <span className="text-white/30 text-[9px] whitespace-nowrap">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Year-over-year trend */}
                {trendData && trendData.labels.length > 1 && (
                  <div className="rounded-xl border border-white/6 p-4 flex flex-col gap-3" style={{ background: '#0a2e18' }}>
                    <p className="text-white/45 text-[10px] font-semibold tracking-[0.12em] uppercase">Year-over-Year Trend</p>
                    <div style={{ height: 140 }}>
                      <Line options={MINI_CHART_OPTS as any} data={trendData} />
                    </div>
                  </div>
                )}

                {/* Outcome breakdown */}
                {outcomeData && (
                  <div className="rounded-xl border border-white/6 p-4 flex flex-col gap-3" style={{ background: '#0a2e18' }}>
                    <p className="text-white/45 text-[10px] font-semibold tracking-[0.12em] uppercase">Case Outcomes</p>
                    <div style={{ height: 140 }}>
                      <Bar options={MINI_CHART_OPTS as any} data={outcomeData} />
                    </div>
                  </div>
                )}
              </div>

              {/* Top locations */}
              {topLocations.length > 0 && (
                <div className="rounded-xl border border-white/6 p-4" style={{ background: '#0a2e18' }}>
                  <p className="text-white/45 text-[10px] font-semibold tracking-[0.12em] uppercase mb-3">Top Seizure Locations</p>
                  <div className="flex flex-col gap-1.5">
                    {topLocations.map(([loc, count]) => (
                      <div key={loc} className="flex items-center gap-3">
                        <span className="text-white/50 text-xs truncate flex-1 min-w-0">{loc}</span>
                        <div className="w-24 shrink-0 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                          <div
                            className="h-full rounded-full bg-green-600"
                            style={{ width: `${(count / topLocations[0][1]) * 100}%` }}
                          />
                        </div>
                        <span className="text-white/30 text-[10px] tabular-nums w-5 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent incidents table */}
              <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
                <div className="px-5 py-3 border-b border-white/5">
                  <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Recent Incidents</p>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: 280, scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr style={{ background: 'rgba(0,0,0,0.15)' }}>
                        {['Location', 'Accused', 'Date', 'Status', ''].map((h) => (
                          <th key={h} className="text-left text-white/25 text-[9px] font-semibold uppercase tracking-wider px-4 py-2.5 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.slice(0, 20).map((c: any, i: number) => {
                        const tracked = c.id && trackedIds.has(c.id);
                        return (
                          <tr key={i} className="border-t border-white/4 hover:bg-white/2">
                            <td className="px-4 py-2.5 text-white/55 truncate max-w-[130px]">{c.location || '—'}</td>
                            <td className="px-4 py-2.5 text-white/45 max-w-[120px]"><MaskedName name={c.nameOfAccused} role={role} /></td>
                            <td className="px-4 py-2.5 text-white/35 whitespace-nowrap">{c.dateOfSeizure || '—'}</td>
                            <td className="px-4 py-2.5">
                              <span className={`text-[10px] font-semibold capitalize ${STATUS_COLOR[c.caseStatus?.toLowerCase()] || 'text-white/35'}`}>
                                {c.caseStatus || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {isLoggedIn ? (
                                <button
                                  onClick={() => c.id && toggle(c.id)}
                                  title={tracked ? 'Untrack case' : 'Track case'}
                                  className={`text-sm leading-none transition-all hover:scale-110 ${tracked ? 'text-green-400' : 'text-white/15 hover:text-white/40'}`}
                                >
                                  {tracked ? '🔔' : '🔕'}
                                </button>
                              ) : (
                                <span title="Log in to track cases" className="text-white/10 text-sm cursor-default">🔕</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
